// EWM-253 — single source of truth for the Algolia `filters` string.
//
// Previously duplicated in `server.ts` (SSR/PLP) and `useAlgoliaProducts`
// (client autocomplete). Two copies meant the two routes could drift on
// scoping rules — dangerous now that the productSelection clause is the
// per-dealer security boundary. One builder, two callers.
//
// Clause order is deterministic so cache keys / snapshots stay stable:
//   category → collection → productSelection → refinements → booleans → lifecycle.

// commercetools lifecycle attribute used to flag discontinued (Z-line)
// products. Hidden by default on browse surfaces; opt-in via the
// "Show discontinued" pill.
//
// EWM-643 — this is the TOP-LEVEL `lifeCycleAttribute`, not the nested
// `productLifeCycleTags.lifeCycleAttribute` it replaced. The nested object is
// RETIRED: it is `null` on every production record, so every clause built from
// it silently matched nothing. That took out three features at once — the GO
// takedown, the discontinued pill, and the New Arrivals collection (which
// matches POSITIVELY on `"N"`, so it rendered empty rather than unfiltered).
// Verified 2026-07-29 against both index generations: the top-level key is
// present and in `attributesForFaceting` on dev AND prod, so it is the only
// one safe to build filters from.
export const LIFECYCLE_FACET_ATTR = 'lifeCycleAttribute';
export const DISCONTINUED_LIFECYCLE_CODE = 'Z';
// EWM-643 — `GO` = removed from sale: a per-country, reversible takedown.
// Unlike the discontinued pill above this exclusion is ALWAYS ON — a GO
// product must never surface on browse/search. The per-region index carries
// the site's own lifecycle tag, so the clause is inherently region-correct.
// Belt-and-braces with the indexer-side exclusion (the index can lag CT) and
// mirrors the BFF's ProductApi region gate (shared engine, EWM-643).
// EWM-944 — re-exported, not redefined. This module and
// `@shared/types/product/regionAvailability` both used to declare 'GO'
// independently and agreed only by luck; the region engine's copy is canonical
// because the BFF gates on it too. Imported as well as re-exported so the
// clause below still binds the name locally.
import { REMOVED_FROM_SALE_LIFECYCLE_CODE } from '@shared/types/product/regionAvailability';
export { REMOVED_FROM_SALE_LIFECYCLE_CODE };

// EWM-239 (OQ-6, Luke 2026-06-13) — the guest "public range" is the wholesale
// CT Product Selection. Confirmed 2026-06-29 against the live target project:
// only two selections exist — `ps-wholesale` (8,876 products) and
// `ps-reseller-full` (8,925); there is NO dedicated `ps-public` selection.
// Guests get the narrower `ps-wholesale` set at RRP pricing (OQ-7). If a
// curated, narrower public range is wanted later, create a new CT selection,
// reindex, and point this constant at it. Single source of truth — also
// consumed by the secured-key mint (`app/[locale]/layout.tsx`) and the PLP
// server tastic (`lib/tastics/product-list`).
export const GUEST_PRODUCT_SELECTION_KEY = 'ps-wholesale';

// Fail-closed sentinel for a logged-in dealer with NO product selection
// configured: scoping the secured key to a key that matches nothing keeps
// autocomplete empty (consistent with the PLP "no selection" state)
// rather than falling open to the whole catalogue.
export const NO_PRODUCT_SELECTION_SENTINEL = '__no_product_selection__';

/**
 * Escape a value before it is embedded in a quoted Algolia filter clause
 * (`<attr>:"<value>"`), so a value containing a quote or a backslash cannot
 * close its own clause and append grammar of its own.
 *
 * EWM-1074 R1-3 — this lived in `lib/collections/config.ts` (for the collection
 * handle, which comes from the URL PATH) and is hoisted here because the facet
 * refinement values, which come from the less-trusted QUERY STRING, need the
 * same treatment and reach Algolia through this module. `collections/config.ts`
 * imports it from here; one escaper, every caller.
 *
 * Measured on `dev_products_sdau`, 2026-09-21: unescaped, the refinement value
 * `x"` produced HTTP 400 (→ the listing's `error` state, whose Retry can never
 * succeed) and `x" OR NOT brand:"x` neutralised the refinement (9,421 hits, the
 * whole in-scope catalogue); escaped, both are inert (HTTP 200, 0 hits).
 *
 * Backslash first, then quote — the other order would re-escape the escape
 * character it just introduced.
 *
 * APPLY AT THE EMITTER ONLY. Now that this is exported, never pre-escape a value
 * you are about to hand to `buildAlgoliaFilters` as a refinement: the emitter
 * escapes every refinement value itself (`:164`), so a pre-escaped one comes out
 * double-escaped and matches nothing. `collections/config.ts` escapes the
 * collection HANDLE, which becomes part of a pre-built `collectionFilter` clause
 * and is deliberately not re-escaped here.
 *
 * `value` is a string by contract and this function does NOT coerce: a non-string
 * reaching it is a bug upstream, and silently `String()`-ing it would hide the
 * bug rather than fix it. The URL side is narrowed at its own boundary
 * (`normalizeSearchParams`, `lib/algolia/server-params.ts` — EWM-1074 R2-1).
 */
export const escapeFilterValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export interface BuildFiltersInput {
  /** commercetools category key → `categories.key:"<key>"`. */
  categoryKey?: string;
  /**
   * EWM-660 — a pre-built Algolia filter clause for a Collection page, e.g.
   * `_collections:"promo-eofy"`, `brand:"Makita"`, or
   * `lifeCycleAttribute:"N"`. Supplied verbatim by the
   * collection route (resolved from its handle) and ANDed with the per-dealer
   * productSelection scoping below, so a collection can never widen the
   * dealer's catalogue. Mutually exclusive with `categoryKey` in practice
   * (a page is either a category or a collection).
   */
  collectionFilter?: string;
  /**
   * Per-dealer catalog scoping keys. OR-joined into
   * `productSelection.productSelection.key:"<k>"`. NOTE: once a secured
   * API key bakes this filter in (EWM-253), this clause is belt-and-
   * braces on the client — the key enforces it server-side regardless —
   * but the SSR path (parent key) still relies on it.
   */
  productSelectionKeys?: string[];
  /** User refinements per attribute, OR within an attribute, AND across. */
  refinements?: Record<string, string[]>;
  /** Boolean facet refinements, e.g. `{ isAvailable: true }`. */
  booleanRefinements?: Record<string, boolean>;
  /**
   * Skip this one refinement attribute — used by the client's disjunctive
   * sibling query so a refined facet keeps its full option list. Never
   * skips category / productSelection (those are scoping, not facets).
   */
  excludeAttr?: string;
  /**
   * When true, append `NOT <lifecycle>:"Z"` to hide discontinued lines.
   * The SSR/PLP path passes this (default-hide); autocomplete omits it to
   * preserve its current show-everything behaviour.
   */
  hideDiscontinued?: boolean;
}

export const buildAlgoliaFilters = ({
  categoryKey,
  collectionFilter,
  productSelectionKeys,
  refinements = {},
  booleanRefinements,
  excludeAttr,
  hideDiscontinued,
}: BuildFiltersInput): string | undefined => {
  const clauses: string[] = [];

  if (categoryKey) clauses.push(`categories.key:"${categoryKey}"`);

  // EWM-660 — collection scoping sits between category and productSelection;
  // wrapped in parens so a compound collection filter (e.g. an OR) can't bleed
  // into the AND-joined dealer scoping that follows.
  if (collectionFilter) clauses.push(`(${collectionFilter})`);

  if (productSelectionKeys && productSelectionKeys.length > 0) {
    const orJoined = productSelectionKeys.map((k) => `productSelection.productSelection.key:"${k}"`).join(' OR ');
    clauses.push(productSelectionKeys.length > 1 ? `(${orJoined})` : orJoined);
  }

  for (const [attribute, values] of Object.entries(refinements)) {
    if (attribute === excludeAttr) continue;
    if (!values || values.length === 0) continue;
    // EWM-1074 R1-3 — values arrive from the URL query string
    // (`facets[<attr>][terms][N]=<value>`, and EWM-927's `?barcode=`, merged in
    // at `resolve-listing-data.ts:199-203` after only whitespace/hyphen
    // stripping). The ATTRIBUTE is bounded by name at the parse boundary
    // (`lib/algolia/filterable-attributes`); the VALUE cannot be, so it is
    // escaped here instead. `categoryKey` / `productSelectionKeys` /
    // `collectionFilter` above are deliberately not escaped: the first two are
    // commercetools-sourced keys and the third is a pre-built clause whose own
    // URL-derived part is already escaped at `collections/config.ts`.
    const orJoined = values.map((v) => `${attribute}:"${escapeFilterValue(v)}"`).join(' OR ');
    clauses.push(values.length > 1 ? `(${orJoined})` : orJoined);
  }

  for (const [attribute, value] of Object.entries(booleanRefinements ?? {})) {
    // Algolia boolean syntax: bare `attribute:true` — no quotes.
    if (value) clauses.push(`${attribute}:true`);
  }

  if (hideDiscontinued) {
    clauses.push(`NOT ${LIFECYCLE_FACET_ATTR}:"${DISCONTINUED_LIFECYCLE_CODE}"`);
  }

  // EWM-643 — removed-from-sale (`GO`) exclusion is unconditional (no pill,
  // no opt-out): the takedown must hold on every browse/search surface.
  clauses.push(`NOT ${LIFECYCLE_FACET_ATTR}:"${REMOVED_FROM_SALE_LIFECYCLE_CODE}"`);

  return clauses.length > 0 ? clauses.join(' AND ') : undefined;
};

/**
 * The per-dealer scoping clause on its own — embedded into the secured
 * API key's `filters` restriction (EWM-253). Kept here so the key and the
 * SSR/client filter strings can never disagree on the productSelection
 * syntax.
 */
export const buildProductSelectionFilter = (productSelectionKeys: string[]): string | undefined => {
  if (!productSelectionKeys || productSelectionKeys.length === 0) return undefined;
  const orJoined = productSelectionKeys.map((k) => `productSelection.productSelection.key:"${k}"`).join(' OR ');
  return productSelectionKeys.length > 1 ? `(${orJoined})` : orJoined;
};
